// Achievement Service - Driving Themed Achievements
// Tracks and awards achievements based on user progress

import { logger } from '../utils/logger';
import { aiCoach } from './aiCoach';
import { gamificationService } from './gamificationService';
import { notificationService } from './notificationService';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string; // Driving-themed icon
  category: 'streak' | 'score' | 'progress' | 'mastery' | 'special';
  unlocked: boolean;
  unlockedDate?: string;
  progress: number; // 0-100
  target: number;
}

export interface AchievementData {
  achievements: Achievement[];
  totalUnlocked: number;
  lastUnlocked?: string;
}

// Achievement definitions - Driving theme
const ACHIEVEMENT_DEFINITIONS: Omit<Achievement, 'unlocked' | 'unlockedDate' | 'progress'>[] = [
  // Streak Achievements
  {
    id: 'streak-3',
    name: 'Getting Started',
    description: 'Maintain a 3-day study streak',
    icon: '🚗',
    category: 'streak',
    target: 3
  },
  {
    id: 'streak-7',
    name: 'Week Warrior',
    description: 'Maintain a 7-day study streak',
    icon: '🏁',
    category: 'streak',
    target: 7
  },
  {
    id: 'streak-30',
    name: 'Monthly Master',
    description: 'Maintain a 30-day study streak',
    icon: '🏆',
    category: 'streak',
    target: 30
  },
  // Score Achievements
  {
    id: 'perfect-score',
    name: 'Perfect Driver',
    description: 'Score 100% on any test',
    icon: '⭐',
    category: 'score',
    target: 100
  },
  {
    id: 'excellent-5',
    name: 'Consistent Excellence',
    description: 'Score 80%+ on 5 tests in a row',
    icon: '🎯',
    category: 'score',
    target: 5
  },
  // Progress Achievements
  {
    id: 'first-test',
    name: 'First Drive',
    description: 'Complete your first practice test',
    icon: '🚦',
    category: 'progress',
    target: 1
  },
  {
    id: 'halfway',
    name: 'Halfway There',
    description: 'Complete 50% of all topics',
    icon: '🛣️',
    category: 'progress',
    target: 50
  },
  {
    id: 'all-topics',
    name: 'Road Master',
    description: 'Complete all 21 practice topics',
    icon: '🛣️',
    category: 'progress',
    target: 21
  },
  // Mastery Achievements
  {
    id: 'master-5',
    name: 'Topic Expert',
    description: 'Master 5 topics (90%+)',
    icon: '🎓',
    category: 'mastery',
    target: 5
  },
  {
    id: 'master-all',
    name: 'Theory Champion',
    description: 'Master all topics (90%+)',
    icon: '👑',
    category: 'mastery',
    target: 21
  },
  // Special Achievements
  {
    id: 'daily-goal-week',
    name: 'Goal Crusher',
    description: 'Complete daily goal 7 days in a row',
    icon: '💪',
    category: 'special',
    target: 7
  },
  {
    id: 'level-10',
    name: 'Level 10 Driver',
    description: 'Reach level 10',
    icon: '🚗',
    category: 'special',
    target: 10
  }
];

class AchievementService {
  private readonly STORAGE_KEY = 'achievements_data';

  /**
   * Initialize achievements
   */
  initialize(): AchievementData {
    const existing = this.loadData();
    if (existing && existing.achievements.length > 0) {
      return this.updateAchievements(existing);
    }

    const achievements: Achievement[] = ACHIEVEMENT_DEFINITIONS.map(def => ({
      ...def,
      unlocked: false,
      progress: 0
    }));

    const data: AchievementData = {
      achievements,
      totalUnlocked: 0
    };

    this.saveData(data);
    return this.updateAchievements(data);
  }

  /**
   * Get all achievements
   */
  getAchievements(): Achievement[] {
    const data = this.initialize();
    return data.achievements;
  }

  /**
   * Get unlocked achievements
   */
  getUnlockedAchievements(): Achievement[] {
    return this.getAchievements().filter(a => a.unlocked);
  }

  /**
   * Check and update achievements based on current progress
   */
  checkAchievements(): Achievement[] {
    const data = this.initialize();
    const updated = this.updateAchievements(data);
    const newlyUnlocked = updated.achievements.filter(
      (a, i) => a.unlocked && !data.achievements[i].unlocked
    );

    if (newlyUnlocked.length > 0) {
      this.saveData(updated);
      logger.info(`Unlocked ${newlyUnlocked.length} achievement(s)`, newlyUnlocked);
      
      // Show notification for newly unlocked achievements
      newlyUnlocked.forEach(achievement => {
        notificationService.showAchievementNotification({
          title: achievement.name,
          description: achievement.description
        });
      });
    }

    return newlyUnlocked;
  }

  /**
   * Update achievement progress
   */
  private updateAchievements(data: AchievementData): AchievementData {
    const testHistory = aiCoach.getTestHistory();
    const testScores = aiCoach.getTestScores();
    const gamificationData = gamificationService.getData();

    const updatedAchievements = data.achievements.map(achievement => {
      let progress = 0;
      let unlocked = achievement.unlocked;

      switch (achievement.id) {
        // Streak achievements
        case 'streak-3':
        case 'streak-7':
        case 'streak-30':
          progress = Math.min(100, (gamificationData.dailyStudyStreak / achievement.target) * 100);
          if (!unlocked && gamificationData.dailyStudyStreak >= achievement.target) {
            unlocked = true;
          }
          break;

        // Score achievements
        case 'perfect-score':
          const hasPerfect = testHistory.some(t => t.percentage === 100);
          progress = hasPerfect ? 100 : 0;
          if (!unlocked && hasPerfect) {
            unlocked = true;
          }
          break;

        case 'excellent-5':
          const recentTests = testHistory.slice(-5);
          const excellentCount = recentTests.filter(t => t.percentage >= 80).length;
          progress = (excellentCount / achievement.target) * 100;
          if (!unlocked && excellentCount >= achievement.target) {
            unlocked = true;
          }
          break;

        // Progress achievements
        case 'first-test':
          progress = testHistory.length > 0 ? 100 : 0;
          if (!unlocked && testHistory.length > 0) {
            unlocked = true;
          }
          break;

        case 'halfway':
        case 'all-topics':
          const completedTopics = Object.keys(testScores).length;
          progress = (completedTopics / achievement.target) * 100;
          if (!unlocked && completedTopics >= achievement.target) {
            unlocked = true;
          }
          break;

        // Mastery achievements
        case 'master-5':
        case 'master-all':
          const masteredTopics = Object.values(testScores).filter(
            s => s.average >= 90
          ).length;
          progress = (masteredTopics / achievement.target) * 100;
          if (!unlocked && masteredTopics >= achievement.target) {
            unlocked = true;
          }
          break;

        // Special achievements
        case 'daily-goal-week':
          // Check last 7 days of daily goal completion
          progress = 50; // Simplified - would need daily challenge history
          break;

        case 'level-10':
          progress = Math.min(100, (gamificationData.level / achievement.target) * 100);
          if (!unlocked && gamificationData.level >= achievement.target) {
            unlocked = true;
          }
          break;
      }

      return {
        ...achievement,
        unlocked,
        progress: Math.min(100, Math.max(0, progress)),
        unlockedDate: unlocked && !achievement.unlockedDate
          ? new Date().toISOString()
          : achievement.unlockedDate
      };
    });

    const totalUnlocked = updatedAchievements.filter(a => a.unlocked).length;
    const lastUnlocked = updatedAchievements
      .filter(a => a.unlocked && a.unlockedDate)
      .sort((a, b) => (b.unlockedDate || '').localeCompare(a.unlockedDate || ''))[0]?.id;

    return {
      achievements: updatedAchievements,
      totalUnlocked,
      lastUnlocked
    };
  }

  /**
   * Load data from storage
   */
  private loadData(): AchievementData | null {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return null;
      return JSON.parse(stored) as AchievementData;
    } catch (error) {
      logger.error('Error loading achievements data:', error);
      return null;
    }
  }

  /**
   * Save data to storage
   */
  private saveData(data: AchievementData): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      logger.error('Error saving achievements data:', error);
    }
  }
}

export const achievementService = new AchievementService();

